import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import LevelBar from '@app/src/components/LevelBar'
import useAssessmentQuestionnaireLink from '@app/src/hooks/useAssessmentQuestionnaireLink'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import Assessment from '@app/src/types/assessment'
import { AssessmentType, Response } from '@app/src/types/resourceExplorer'
import { Box, Breadcrumbs, CircularProgress, Divider, Link, Paper, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { Link as RouterLink, useParams } from 'react-router-dom'
import BaselineAssessmentDetailedReview from '../BaselineAssessmentDetailedReview'
import BaselineAssessmentSectionRating from '../BaselineAssessmentSectionRating'

const staleTime = 60000

const TransparencyBaselineAssessmentPage: React.FC = () => {
  const { formatMessage } = useIntl()
  const { id } = useParams<{ id: string }>()

  const { items: fetchedAssessments } = useFetchCollectionWithPost<Assessment>({
    key: [FetchKey.AssessmentCollection, id],
    endpoint: endpoints.assessmentFilterOwnOrganization,
    payload: {
      filter: [
        {
          name: 'id',
          filters: [{ operator: Operators.EqualTo, value: id }],
        },
      ],
      include: ['assessmentTemplate.sections.rules'],
    },
    options: {
      enabled: Boolean(id),
      staleTime,
    },
  })

  const assessment = fetchedAssessments?.[0]

  const { assessmentQuestionnaireLink } = useAssessmentQuestionnaireLink({
    includeAssessment: false,
    includeQuestionnaire: false,
    assessmentType: AssessmentType.BaselineAssessment,
  })

  const periodFilterValue = assessment?.periodName

  const {
    items: [response],
    isLoading: responseIsLoading,
  } = useFetchCollectionWithPost<Response>({
    key: FetchKey.Response,
    endpoint: endpoints.responsesCollection,
    payload: {
      filter: [
        {
          name: 'request.questionnaireTemplateId',
          filters: [
            {
              value: assessmentQuestionnaireLink?.questionnaireTemplateId,
              operator: Operators.EqualTo,
            },
          ],
        },
        {
          name: 'isLatestSubmitted',
          filters: [
            {
              value: true,
              operator: Operators.EqualTo,
            },
          ],
        },
        {
          name: 'request.periodName',
          filters: [
            {
              value: periodFilterValue,
              operator: Operators.EqualTo,
            },
          ],
        },
      ],
      include: ['items.requestItem.template.RequestCollection', 'items.files'],
    },
    options: {
      enabled: Boolean(assessmentQuestionnaireLink && periodFilterValue),
      staleTime: 60000,
    },
  })

  if (!assessment) {
    return (
      <Stack flexGrow={1} justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Stack>
    )
  }

  if (responseIsLoading) {
    return (
      <Stack flexGrow={1} justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Stack>
    )
  }

  if (!responseIsLoading && response && assessment) {
    return (
      <>
        <Box
          sx={{
            bgcolor: 'grey.200',
            height: '48px',
            px: 5,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Breadcrumbs aria-label="breadcrumb">
            <Link component={RouterLink} to="/transparency/dashboard" color="inherit" underline="hover">
              {formatMessage({ id: 'general.home' })}
            </Link>
            <Typography color="text.primary">{assessment.assessmentTemplate.name}</Typography>
          </Breadcrumbs>
        </Box>
        <Box
          sx={{
            bgcolor: 'grey.50',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            width: '100%',
            pb: 4,
          }}
        >
          <Box width={800} mb={2} mt={3}>
            <Typography variant="h2">{assessment.assessmentTemplate.name}</Typography>
          </Box>
          <Box width={800}>
            <Paper elevation={0} sx={{ px: 3, py: 4, mt: 2 }}>
              <Stack spacing={2} width="100%">
                <Box>
                  <Box display="flex" alignItems="center">
                    <Typography variant="h2" mr={2}>
                      {formatMessage({ id: 'baselineAssessment.totalLevel' }, { value: assessment.totalLevel })}
                    </Typography>
                    <LevelBar value={assessment.totalLevel} size="large" />
                  </Box>
                  <Typography mr={2} mt={1} variant="subtitle1">
                    {formatMessage({ id: `baselineAssessment.levelDefinitions.level${assessment.totalLevel}Title` })}
                  </Typography>
                  <Typography mr={2}>
                    {formatMessage({
                      id: `baselineAssessment.levelDefinitions.level${assessment.totalLevel}HelperText`,
                    })}
                  </Typography>
                </Box>
                <Stack direction="row" width="100%" maxWidth={700} justifyContent="space-between">
                  <BaselineAssessmentSectionRating
                    sectionName={formatMessage({ id: 'baselineAssessment.section1' })}
                    level={assessment.section1Level}
                  />
                  <Divider flexItem orientation="vertical" />
                  <BaselineAssessmentSectionRating
                    sectionName={formatMessage({ id: 'baselineAssessment.section2' })}
                    level={assessment.section2Level}
                  />
                  <Divider flexItem orientation="vertical" />
                  <BaselineAssessmentSectionRating
                    sectionName={formatMessage({ id: 'baselineAssessment.section3' })}
                    level={assessment.section3Level}
                  />
                  <Divider flexItem orientation="vertical" />
                  <BaselineAssessmentSectionRating
                    sectionName={formatMessage({ id: 'baselineAssessment.section4' })}
                    level={assessment.section4Level}
                  />
                  <Divider flexItem orientation="vertical" />
                  <BaselineAssessmentSectionRating
                    sectionName={formatMessage({ id: 'baselineAssessment.section5' })}
                    level={assessment.section5Level}
                  />
                </Stack>
              </Stack>
            </Paper>
          </Box>
          <Box width={800}>
            <BaselineAssessmentDetailedReview latestResponse={response} assessment={assessment} />
          </Box>
        </Box>
      </>
    )
  }

  return null
}

export default TransparencyBaselineAssessmentPage
